/**
 * Safe localStorage wrapper to handle SecurityError and other exceptions
 * that may occur in private browsing mode or restricted environments
 */

export const safeStorage = {
  /**
   * Safely get an item from localStorage
   * @param {string} key - The key to retrieve
   * @param {*} defaultValue - Default value if retrieval fails
   * @returns {string|null} The stored value or defaultValue
   */
  getItem: (key, defaultValue = null) => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return defaultValue;
      }
      const value = localStorage.getItem(key);
      return value !== null ? value : defaultValue;
    } catch (error) {
      console.warn(`Failed to get localStorage item "${key}":`, error.message);
      return defaultValue;
    }
  },

  /**
   * Safely set an item in localStorage
   * @param {string} key - The key to store
   * @param {string} value - The value to store
   * @returns {boolean} Success status
   */
  setItem: (key, value) => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.warn(`Failed to set localStorage item "${key}":`, error.message);
      return false;
    }
  },

  /**
   * Safely remove an item from localStorage
   * @param {string} key - The key to remove
   * @returns {boolean} Success status
   */
  removeItem: (key) => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Failed to remove localStorage item "${key}":`, error.message);
      return false;
    }
  },

  /**
   * Safely clear all localStorage
   * @returns {boolean} Success status
   */
  clear: () => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      localStorage.clear();
      return true;
    } catch (error) {
      console.warn('Failed to clear localStorage:', error.message);
      return false;
    }
  },

  /**
   * Check if localStorage is available
   * @returns {boolean} Availability status
   */
  isAvailable: () => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }
};

// Also export sessionStorage wrapper
export const safeSessionStorage = {
  getItem: (key, defaultValue = null) => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return defaultValue;
      }
      const value = sessionStorage.getItem(key);
      return value !== null ? value : defaultValue;
    } catch (error) {
      console.warn(`Failed to get sessionStorage item "${key}":`, error.message);
      return defaultValue;
    }
  },

  setItem: (key, value) => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return false;
      }
      sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.warn(`Failed to set sessionStorage item "${key}":`, error.message);
      return false;
    }
  },

  removeItem: (key) => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return false;
      }
      sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Failed to remove sessionStorage item "${key}":`, error.message);
      return false;
    }
  },

  clear: () => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return false;
      }
      sessionStorage.clear();
      return true;
    } catch (error) {
      console.warn('Failed to clear sessionStorage:', error.message);
      return false;
    }
  },

  isAvailable: () => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return false;
      }
      const testKey = '__sessionStorage_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }
};